import type { FC, JSX, Ref, SVGProps } from 'react';

import type { LucideIcon } from 'lucide-react';
import { Loader2 } from 'lucide-react';

import { Button, type ButtonProps } from '@shared/components/ui';
import { cn } from '@shared/utils';

type SVGIconComponent = (props: SVGProps<SVGSVGElement>) => JSX.Element;

export interface GenericButtonProps extends ButtonProps {
  label?: string;
  isLoading?: boolean;
  icon?: LucideIcon | SVGIconComponent;
  iconPosition?: 'left' | 'right';
  ref?: Ref<HTMLButtonElement>;
}

export const GenericButton: FC<GenericButtonProps> = ({
  label,
  isLoading = false,
  icon: Icon,
  iconPosition = 'left',
  className,
  variant = 'default',
  disabled,
  onClick,
  children,
  ref,
  ...props
}) => {
  const isDisabled = disabled ?? isLoading;

  const handleClick: GenericButtonProps['onClick'] = (e) => {
    if (isDisabled) return;
    onClick?.(e);
  };
  const isIconOnly = !label;

  function renderButtonContent() {
    if (isLoading) {
      return (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {label && <span>{label}</span>}
        </>
      );
    }
    if (isIconOnly && Icon) {
      return <Icon className="h-4 w-4" />;
    }
    return (
      <>
        {Icon && iconPosition === 'left' && <Icon className="h-4 w-4" />}
        {label}
        {Icon && iconPosition === 'right' && <Icon className="h-4 w-4" />}
      </>
    );
  }

  return (
    <Button
      ref={ref}
      variant={variant}
      disabled={isDisabled}
      onClick={handleClick}
      className={cn('gap-2', isIconOnly && 'p-0 w-8 h-8 flex items-center justify-center', className)}
      {...props}
    >
      {renderButtonContent()}
      {children}
    </Button>
  );
};
